import { extractRawPlanKey } from "@landlordops/shared";
import { isGrandfatheredScaleSuite } from "./grandfathering";

/** Same field precedence as backend `effectiveSubscriptionPlanKey` / shared `extractRawPlanKey`. */
function rawSubscriptionPlanKey(userInfo) {
  return String(extractRawPlanKey(userInfo || {}) || "")
    .trim()
    .toLowerCase();
}

/** Exported for UI that builds tier labels (e.g. Units) so caps match backend. */
export function getRawSubscriptionPlanKey(userInfo) {
  return rawSubscriptionPlanKey(userInfo);
}

export const TierKey = {
  FREE: "free",
  STARTER: "starter",
  GROWTH: "growth",
  SCALE: "scale",
  ENTERPRISE: "enterprise",
  /** @deprecated Legacy 2025 only */
  PRO: "pro",
  /** @deprecated Legacy 2025 only */
  BUSINESS: "business",
};

/**
 * Maps UI / suite plan keys to Stripe `selectedPlan` for POST /stripe/create-checkout-session
 * (same rules as IntakeModal `deriveCheckoutPlanKey`).
 */
export function getCheckoutPlanKeyForStripe(displayPlan) {
  const p = String(displayPlan || "")
    .trim()
    .toLowerCase();
  // Legacy chat_* SKUs still resolve, but checkout now writes suite keys so
  // every paid band provisions the full LandlordOps OS (door caps only differ).
  if (p === "chat_starter" || p === "starter") return "starter";
  if (
    p === "chat_growth" ||
    p === "chat_business" ||
    p === "chat_pro" ||
    p === "growth" ||
    p === "pro"
  ) {
    return "growth";
  }
  if (p === "chat_scale" || p === "business" || p === "scale") return "scale";
  return "starter";
}

/**
 * Free tier: the 3-Unit Free Gateway.
 * Product-led growth entry point. No credit card, no Stripe subscription.
 * Includes the full LandlordOps OS (rent collection, Schedule E, vendors, ledgers, Leo).
 * Tiers differ only by door caps. Hard cap: 3 doors. Enforced on POST /api/units
 * and rent-roll import by the tier-limits middleware.
 */
export const FREE_TIER = Object.freeze({
  key: "free",
  name: "Free",
  monthlyUSD: 0,
  unitCap: 3,
  stripePriceId: null,
  tagline: "Full OS for 1-3 doors. No card.",
  ctaLabel: "Start for Free",
  /** Second line of the Free CTA button (pricing grid, subscription modal). */
  ctaSubline: "(No credit card)",
  features: Object.freeze([
    "1-3 units",
    "Automated Rent Collection",
    "Schedule E Tax Reporting",
    "Vendor Management",
    "Ledger Syncing",
    "Leo AI Assistant",
  ]),
  lockedFeatures: Object.freeze(["Multi-user / team access"]),
});

/**
 * Granular permission flags for the unified value ladder.
 * Every self-serve tier grants the full LandlordOps OS. Door caps are the only
 * differentiator (Free 3 · Starter 15 · Growth 50 · Scale 150).
 */
export const Permission = {
  LEO_CHAT: "leo_chat",
  EMAIL_ALERTS: "email_alerts",
  SMS_ALERTS: "sms_alerts",
  VOICE_ALERTS: "voice_alerts",
  ADVANCED_TRIAGE: "advanced_triage",
  RENT_COLLECTION: "rent_collection",
  VENDOR_BIDDING: "vendor_bidding",
  TAX_PREP: "tax_prep",
  CLOSING_VAULT: "closing_vault",
};

/** Full OS surface - granted on Free through Enterprise. */
const _FULL_OS_PERMS = [
  Permission.LEO_CHAT,
  Permission.EMAIL_ALERTS,
  Permission.SMS_ALERTS,
  Permission.VOICE_ALERTS,
  Permission.ADVANCED_TRIAGE,
  Permission.RENT_COLLECTION,
  Permission.VENDOR_BIDDING,
  Permission.TAX_PREP,
  Permission.CLOSING_VAULT,
];

/** Tier key -> frozen set of Permission values. */
export const TIER_PERMISSIONS = Object.freeze({
  [TierKey.FREE]: Object.freeze(new Set(_FULL_OS_PERMS)),
  [TierKey.STARTER]: Object.freeze(new Set(_FULL_OS_PERMS)),
  [TierKey.GROWTH]: Object.freeze(new Set(_FULL_OS_PERMS)),
  [TierKey.SCALE]: Object.freeze(new Set(_FULL_OS_PERMS)),
  [TierKey.ENTERPRISE]: Object.freeze(new Set(_FULL_OS_PERMS)),
  [TierKey.PRO]: Object.freeze(new Set(_FULL_OS_PERMS)),
  [TierKey.BUSINESS]: Object.freeze(new Set(_FULL_OS_PERMS)),
});

/**
 * Check whether a normalized tier key grants a specific Permission.
 * Falls back to STARTER when the key is unrecognised.
 */
export function tierHasPermission(tierKey, permission) {
  const perms = TIER_PERMISSIONS[tierKey] || TIER_PERMISSIONS[TierKey.STARTER];
  return perms.has(permission);
}

/**
 * Return the full set of permissions for a tier key.
 */
export function getTierPermissions(tierKey) {
  return TIER_PERMISSIONS[tierKey] || TIER_PERMISSIONS[TierKey.STARTER];
}

/** Legacy 4-tier suite (pre OpsChat-first); used when pricingTierGroup === "legacy_2025". */
/** @deprecated Use tier-specific features from getPricingSchedule; kept for legacy billing UI. */
export const SHARED_PLAN_FEATURES = [
  "Leo AI for tenant chat",
  "Rent collection & automated reminders",
  "Late-fee rules & tracking",
  "Maintenance triage & vendor routing",
  "Leasing funnel: prospect chat & tour booking",
  "Deal Analyzer: DSCR, NOI, investor analytics",
  "Schedule E & tax-ready exports",
  "Portfolio dashboard & reporting",
];

function planFeaturesLegacy(unitCap) {
  return [`Up to ${unitCap} doors`, ...SHARED_PLAN_FEATURES];
}

export const PRICING = {
  starter: {
    name: "Starter",
    monthlyUSD: 19,
    unitCap: 2,
    stripePriceId: "price_starter_19",
    tagline: "Perfect for small portfolios",
    features: planFeaturesLegacy(2),
  },
  pro: {
    name: "Pro",
    monthlyUSD: 79,
    unitCap: 20,
    stripePriceId: "price_pro_79",
    tagline: "Best for growing portfolios",
    popular: true,
    features: planFeaturesLegacy(20),
  },
  business: {
    name: "Business",
    monthlyUSD: 189,
    unitCap: 50,
    stripePriceId: "price_business_189",
    tagline: "For expanding portfolios",
    features: planFeaturesLegacy(50),
  },
  growth: {
    name: "Growth",
    monthlyUSD: 299,
    unitCap: 100,
    stripePriceId: "price_growth_299",
    tagline: "For larger portfolios",
    features: planFeaturesLegacy(100),
  },
  /**
   * Legacy catalog: same full-OS band as market_2026 Scale. Former "Business" tier
   * subscribers resolve here via {@link normalizePlanKeyForSchedule}.
   */
  scale: {
    name: "Scale",
    monthlyUSD: 329,
    unitCap: 100,
    stripePriceId: "price_scale_329",
    tagline: "Full LandlordOps OS, including legacy Business-tier accounts",
    features: planFeaturesLegacy(100),
  },
};

/**
 * IMPORTANT: For Starter / Growth / Scale the first entry MUST read "Up to N units…"
 * because PricingSection and ExclusiveLegacyPricingShared use `plan.features[0]` as the
 * visible unit-range label. Enterprise card does not share this constraint.
 *
 * Every paid tier lists the same full-OS core; door caps are the differentiator.
 */
const FULL_OS_CORE_FEATURES = Object.freeze([
  "Automated Rent Collection",
  "Schedule E Tax Reporting",
  "Vendor Management",
  "Ledger Syncing",
  "Leo AI Assistant",
]);

const STARTER_FEATURES = ["Up to 15 doors", ...FULL_OS_CORE_FEATURES];

const GROWTH_FEATURES = ["Up to 50 doors", ...FULL_OS_CORE_FEATURES];

const SCALE_FEATURES = [
  "Up to 150 doors",
  ...FULL_OS_CORE_FEATURES,
];

const ENTERPRISE_FEATURES = [
  "Up to 250 units: portfolio-wide flat rate",
  ...FULL_OS_CORE_FEATURES,
  "Dedicated migration support",
  "Multi-entity LLC accounting",
];

/**
 * Unit cap preserved for users grandfathered onto the Scale suite before the
 * April 2026 pricing change. New Scale subscribers use the current Scale cap (150 doors) in {@link PRICING_2026}.
 */
export const GRANDFATHERED_SCALE_UNIT_CAP = 200;

/**
 * Fixed tier order for legacy billing UI (Profile, subscription modal when on legacy catalog).
 */
export const PLAN_KEY_ORDER_LEGACY_2025 = ["starter", "pro", "growth", "scale"];

/**
 * Fixed tier order for market 2026 ladder. Use everywhere the modal / grids must
 * list tiers in sequence (Enterprise last). Not derived from Object.keys order.
 */
export const PLAN_KEY_ORDER_2026 = ["starter", "growth", "scale", "enterprise"];

/**
 * OpsChat-first market tiers (effective April 1, 2026+ for market_2026).
 * Value-Capture 2026: Starter $99 / Growth $249 / Scale $599.
 * Door caps: 15 / 50 / 150 (Starter / Growth / Scale).
 * Free ($0, 3 doors) and Enterprise (quote-based, up to 250 doors)
 * are exposed separately (FREE_TIER / ENTERPRISE_TIER) because they are not
 * backed by a standard Stripe SKU and should not leak into checkout flows.
 * Keys: starter | growth | scale (internal subscription / Stripe metadata).
 */
export const PRICING_2026 = {
  starter: {
    name: "Starter",
    monthlyUSD: 99,
    unitCap: 15,
    stripePriceId: "price_starter_99_2026",
    tagline: "Full LandlordOps OS · up to 15 doors.",
    features: STARTER_FEATURES,
  },
  growth: {
    name: "Growth",
    monthlyUSD: 249,
    unitCap: 50,
    stripePriceId: "price_growth_249_2026",
    popular: true,
    description:
      "Full LandlordOps OS for mid-size portfolios - same features as every tier, more doors.",
    tagline: "Full LandlordOps OS · up to 50 doors.",
    features: GROWTH_FEATURES,
  },
  scale: {
    name: "Scale",
    monthlyUSD: 599,
    unitCap: 150,
    stripePriceId: "price_scale_599_2026",
    featured: true,
    description:
      "Full LandlordOps OS for larger portfolios - same features as every tier, more doors.",
    tagline: "Full LandlordOps OS · up to 150 doors.",
    features: SCALE_FEATURES,
  },
  /** Self-serve display: numeric price + unit cap required so UI filters never drop this card. */
  enterprise: {
    name: "Enterprise",
    monthlyUSD: 599,
    unitCap: 250,
    stripePriceId: "price_enterprise_599_2026",
    tagline: "Institutional Excellence.",
    features: ENTERPRISE_FEATURES,
  },
};

/**
 * Annual list totals for market_2026 marketing UI (2 months free vs 12× monthly);
 * must match Stripe annual Price objects in the Dashboard.
 */
export const ANNUAL_DISPLAY_USD_2026 = {
  starter: 990,
  growth: 2490,
  scale: 5990,
};

/**
 * @param {string} planKey, e.g. starter, growth, scale, pro, business
 * @param {string} [pricingTierGroup]
 * @returns {number | null} null if annual is not self-serve for this row
 */
export function getAnnualDisplayUsd(planKey, pricingTierGroup = "market_2026") {
  const k = String(planKey || "").toLowerCase();
  if (pricingTierGroup === "market_2026") {
    if (k in ANNUAL_DISPLAY_USD_2026) {
      return ANNUAL_DISPLAY_USD_2026[k];
    }
    return null;
  }
  if (pricingTierGroup === "legacy_2025") {
    const schedule = getPricingSchedule("legacy_2025");
    const row = schedule[k];
    if (!row || typeof row.monthlyUSD !== "number") return null;
    return Math.round(row.monthlyUSD * 10);
  }
  return null;
}

/** Formats whole-dollar annual amounts with grouping (e.g. 1,690). */
export function formatAnnualDisplayUsd(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return String(amount);
  return n.toLocaleString("en-US");
}

/**
 * Shared ROI copy used by the subscription modal sub-header, home pricing section,
 * and upgrade surfaces. Sourced here so messaging stays in lockstep across UIs.
 * Every tier includes the full OS; door caps are the only differentiator.
 */
export const ROI_SUBHEADER_COPY =
  "Every plan includes the full LandlordOps OS - rent collection, Schedule E, vendors, ledgers, and Leo. You only pay for the door count you need.";

/** Surfaced in the PricingSection tooltip and inside the subscription modal ROI band. */
export const COST_OF_VACANCY_TOOLTIP =
  "Free (1-3), Starter (15), Growth (50), and Scale (150) all unlock the same Rental Command Center. Upgrade only when you need more doors.";

/** "Labor Comparison" value badges for the subscription modal Scale / Enterprise cards. */
export const TIER_VALUE_BADGES_2026 = {
  scale: "Cost: ~40% of a part-time VA",
  enterprise: "Saves $5,000+/yr in overhead",
};

/** Per-tier "Est. Labor Hours Saved" row for FeatureMatrix (Free through Scale). */
export const TIER_LABOR_HOURS_SAVED_2026 = {
  free: "2+ hrs / mo",
  starter: "5+ hrs / mo",
  growth: "15+ hrs / mo",
  scale: "30+ hrs / mo",
};

const PRICING_2026_EFFECTIVE_DATE = new Date("2026-04-01T00:00:00.000Z");

function is2026PricingEffective(asOfDate = new Date()) {
  return new Date(asOfDate) >= PRICING_2026_EFFECTIVE_DATE;
}

export function getTierByKey(tierKey) {
  return PRICING[tierKey];
}

export function getTierByPriceId(stripePriceId) {
  const all = { ...PRICING, ...PRICING_2026 };
  return (
    Object.values(all).find((tier) => tier.stripePriceId === stripePriceId) ||
    null
  );
}

/**
 * Recommend tier by door count for the active schedule.
 */
export function getTierByUnitCount(
  unitCount,
  pricingTierGroup = "market_2026",
  asOfDate = new Date()
) {
  const schedule = getPricingSchedule(pricingTierGroup, asOfDate);
  const tiers = Object.values(schedule).sort(
    (a, b) => (a.unitCap || 0) - (b.unitCap || 0)
  );
  for (const t of tiers) {
    if (unitCount <= (t.unitCap ?? 0)) return t;
  }
  return null;
}

export function formatMonthlyUsd(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return String(amount);
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

/**
 * Maps subscription / checkout plan string to a schedule key on market_2026.
 */
export function normalizePlanKeyForSchedule(
  sub,
  pricingTierGroup = "market_2026"
) {
  const s = String(sub || "starter")
    .trim()
    .toLowerCase();
  if (s === "free") return "free";
  if (s === "enterprise") return "enterprise";
  // Legacy suite "Business" (pre-OpsChat groups) → Scale row for caps & UI
  if (s === "business") return "scale";
  if (pricingTierGroup === "legacy_2025") {
    const valid = ["starter", "pro", "growth", "scale"];
    return valid.includes(s) ? s : "starter";
  }
  if (s === "scale" || s === "chat_scale") return "scale";
  if (
    s === "growth" ||
    s === "chat_growth" ||
    s === "pro" ||
    s === "chat_pro" ||
    s === "chat_business"
  ) {
    return "growth";
  }
  if (s === "starter" || s === "chat_starter" || s === "chat_only") {
    return "starter";
  }
  if (s.startsWith("chat_")) return "starter";
  return "starter";
}

/**
 * OpsChat checkout tiers: parallel Stripe SKUs (chat_*); caps align with suite tiers.
 */
export function getOpsChatIntakeTiers(pricingTierGroup = "market_2026") {
  if (pricingTierGroup === "legacy_2025") {
    const schedule = getPricingSchedule(pricingTierGroup);
    const rows = [
      { planKey: "chat_starter", suiteKey: "starter", monthlyUSD: 19.99 },
      { planKey: "chat_pro", suiteKey: "pro", monthlyUSD: 29.99 },
      { planKey: "chat_business", suiteKey: "scale", monthlyUSD: 39.99 },
      { planKey: "chat_growth", suiteKey: "growth", monthlyUSD: 49.99 },
    ];
    return rows.map(({ planKey, suiteKey, monthlyUSD }) => {
      const s = schedule[suiteKey];
      return {
        planKey,
        label: s.name,
        monthlyUSD,
        unitCap: s.unitCap,
        tagline: s.tagline,
        popular: suiteKey === "pro",
      };
    });
  }

  const schedule = getPricingSchedule(pricingTierGroup);
  const rows = [
    { planKey: "starter", suiteKey: "starter" },
    { planKey: "growth", suiteKey: "growth" },
    { planKey: "scale", suiteKey: "scale" },
  ];
  return rows
    .map(({ planKey, suiteKey }) => {
      const s = schedule[suiteKey];
      if (!s) return null;
      return {
        planKey,
        label: s.name,
        monthlyUSD: s.monthlyUSD,
        unitCap: s.unitCap,
        tagline: s.tagline,
        popular: suiteKey === "growth",
      };
    })
    .filter(Boolean);
}

export function getEffectiveUnitCap(
  userInfo,
  pricingTierGroup = "market_2026"
) {
  // Grandfathered users retain the pre-April-2026 200-unit cap regardless of
  // the current Scale tier cap (now 150 for new subscribers).
  if (userInfo && isGrandfatheredScaleSuite(userInfo)) {
    return GRANDFATHERED_SCALE_UNIT_CAP;
  }
  const sub = rawSubscriptionPlanKey(userInfo) || "starter";
  if (sub === "free") return FREE_TIER.unitCap;
  if (sub === "enterprise") return ENTERPRISE_TIER.unitCap;
  if (sub === "chat_only") {
    const c = Number(userInfo?.chatOnlyUnitCap);
    return Number.isFinite(c) && c > 0 ? c : 2;
  }
  const schedule = getPricingSchedule(pricingTierGroup);
  const key = normalizePlanKeyForSchedule(sub, pricingTierGroup);
  return schedule[key]?.unitCap ?? schedule.starter.unitCap;
}

/**
 * True when the user's stored plan is the 3-Unit Free Gateway.
 * Explicit "free" only: users without any plan string stay on "starter" so legacy
 * accounts that pre-date the Free tier don't silently get down-capped.
 */
export function isFreeTierUser(userInfo) {
  return rawSubscriptionPlanKey(userInfo) === "free";
}

export function canAddUnit(userTier, currentUnits, pricingTierGroup, userInfo) {
  const sub =
    rawSubscriptionPlanKey(userInfo) ||
    String(userTier || "")
      .trim()
      .toLowerCase();
  if (sub === "free") {
    return currentUnits < FREE_TIER.unitCap;
  }
  if (sub === "enterprise") {
    return currentUnits < ENTERPRISE_TIER.unitCap;
  }
  if (sub === "chat_only") {
    const cap = getEffectiveUnitCap(userInfo, pricingTierGroup);
    return currentUnits < cap;
  }
  if (userInfo && isGrandfatheredScaleSuite(userInfo)) {
    return currentUnits < GRANDFATHERED_SCALE_UNIT_CAP;
  }
  const schedule = getPricingSchedule(pricingTierGroup || "market_2026");
  const key = normalizePlanKeyForSchedule(
    String(userTier || "starter").toLowerCase(),
    pricingTierGroup || "market_2026"
  );
  const unitCap = schedule[key]?.unitCap ?? schedule.starter.unitCap;
  return currentUnits < unitCap;
}

export function getRecommendedTier(unitCount, pricingTierGroup, asOfDate) {
  return getTierByUnitCount(
    unitCount,
    pricingTierGroup || "market_2026",
    asOfDate
  );
}

/** Next self-serve tier name for upgrade CTAs (e.g. Doors page tooltip). */
export function getNextTierUpgradeName(
  currentNormalizedKey,
  pricingTierGroup = "market_2026"
) {
  if (pricingTierGroup === "legacy_2025") {
    const order = PLAN_KEY_ORDER_LEGACY_2025;
    const k = String(currentNormalizedKey || "starter").toLowerCase();
    const i = order.indexOf(k);
    if (i < 0 || i >= order.length - 1) return null;
    return PRICING[order[i + 1]]?.name || order[i + 1];
  }
  const order = PLAN_KEY_ORDER_2026;
  const k = String(currentNormalizedKey || "starter").toLowerCase();
  const i = order.indexOf(k);
  if (i < 0 || i >= order.length - 1) return null;
  return PRICING_2026[order[i + 1]]?.name || order[i + 1];
}

export const LEGACY_PLAN_MAPPING = {
  Starter: "starter",
  Pro: "pro",
  "OpsAI Pro": "growth",
  Enterprise: "growth",
  Custom: "growth",
};

export function mapLegacyPlan(legacyPlanName) {
  return LEGACY_PLAN_MAPPING[legacyPlanName] || null;
}

/**
 * Legacy mailto for Enterprise migration inquiries (optional deep links / docs).
 * The live "Schedule Migration" CTA uses {@link getEnterpriseSalesHref} instead.
 */
export const ENTERPRISE_CONTACT_URL =
  "mailto:sales@landlordops.com?subject=LandlordOps%20Enterprise%20Migration%20Inquiry&body=Hi%20LandlordOps%20team%2C%0A%0AI%27d%20like%20to%20schedule%20a%20migration%20conversation%20for%20our%20portfolio.%0A%0APortfolio%20size%3A%20%0ACurrent%20software%3A%20%0ATimeline%3A%20%0A%0AThanks%2C%0A";

/** Default in-app route for Enterprise "Schedule Migration" (same-origin). */
export const ENTERPRISE_MIGRATION_CTA_PATH = "/contact";

/**
 * Target for Enterprise "Schedule Migration" (quote-based, not self-serve checkout).
 * `REACT_APP_ENTERPRISE_CTA_HREF` overrides (e.g. full mailto or external URL); otherwise `/contact`.
 */
export function getEnterpriseSalesHref() {
  if (typeof process === "undefined") return ENTERPRISE_MIGRATION_CTA_PATH;
  return (
    process.env.REACT_APP_ENTERPRISE_CTA_HREF || ENTERPRISE_MIGRATION_CTA_PATH
  );
}

/** Opens Enterprise sales: same tab for app paths/mailto, new tab for http(s) links. */
export function openEnterpriseSalesWindow() {
  if (typeof window === "undefined") return;
  const href = getEnterpriseSalesHref();
  if (/^mailto:|^tel:/i.test(href)) {
    window.location.href = href;
  } else if (/^https?:\/\//i.test(href)) {
    window.open(href, "_blank", "noopener,noreferrer");
  } else {
    window.location.assign(href);
  }
}

/**
 * Enterprise tier lives alongside PRICING_2026 rather than inside it so
 * paid-checkout flows (IntakeModal, UpgradeModal, Stripe helpers) never
 * accidentally try to run a Stripe session against a quote-based tier.
 *
 * type: "contact": any UI component should replace the Stripe button with
 * contactUrl (see getEnterpriseSalesHref) when it sees this flag.
 * priceLabel / startingAtLabel: use for display (Enterprise priceLabel is null:
 * no dollar amount in marketing UI); monthlyUSD is kept for analytics and
 * backend TIER_LADDER parity only.
 *
 * Homepage PricingSection renders Enterprise as a full-width sidecar (not a grid card).
 * /platform (`variant="fullOs"`) adds Enterprise as a second grid card next to Scale.
 * SubscriptionModal renders it as a grid column (5th) using getDisplayPricingLadder.
 */
export const ENTERPRISE_TIER = Object.freeze({
  key: "enterprise",
  name: "Enterprise",
  type: "contact",
  monthlyUSD: 599,
  priceLabel: null,
  startingAtLabel: "Contact us for a custom quote",
  unitCap: 1000,
  stripePriceId: null,
  contactUrl: ENTERPRISE_MIGRATION_CTA_PATH,
  ctaLabel: "Schedule Migration",
  tagline: "Institutional Excellence for 150+ door operators.",
  features: Object.freeze([
    "Everything in Scale, plus dedicated account manager",
    "Custom AI workflows and advanced integrations",
    "Priority onboarding, migration support, and SLA guarantees",
    "White-label options and institutional-grade reporting",
  ]),
});

export function getPricingSchedule(pricingTierGroup, asOfDate = new Date()) {
  if (pricingTierGroup === "legacy_2025") return PRICING;
  return is2026PricingEffective(asOfDate) ? PRICING_2026 : PRICING;
}

/**
 * Pricing schedule with the 3-Unit Free Gateway prepended at `free`.
 * Use this when building UI (e.g. SubscriptionModal grid) where Free should
 * appear alongside paid tiers. Backend enforcement reads FREE_TIER directly.
 */
export function getPricingScheduleWithFree(
  pricingTierGroup,
  asOfDate = new Date()
) {
  return {
    free: {
      key: "free",
      name: FREE_TIER.name,
      monthlyUSD: FREE_TIER.monthlyUSD,
      unitCap: FREE_TIER.unitCap,
      stripePriceId: FREE_TIER.stripePriceId,
      tagline: FREE_TIER.tagline,
      features: FREE_TIER.features,
    },
    ...getPricingSchedule(pricingTierGroup, asOfDate),
  };
}

/**
 * Full display ladder for the public homepage and in-app billing grid:
 * Free ($0) · Starter · Growth · Scale · Enterprise (5 columns).
 * Use ONLY for rendering. Stripe checkout flows must keep using
 * {@link getPricingSchedule} so the Enterprise "quote" row never reaches
 * Stripe.
 */
export function getDisplayPricingLadder(
  pricingTierGroup,
  asOfDate = new Date()
) {
  if (pricingTierGroup === "legacy_2025") {
    return getPricingScheduleWithFree(pricingTierGroup, asOfDate);
  }
  return {
    ...getPricingScheduleWithFree(pricingTierGroup, asOfDate),
    enterprise: {
      key: "enterprise",
      name: ENTERPRISE_TIER.name,
      type: ENTERPRISE_TIER.type,
      monthlyUSD: ENTERPRISE_TIER.monthlyUSD,
      priceLabel: ENTERPRISE_TIER.priceLabel,
      startingAtLabel: ENTERPRISE_TIER.startingAtLabel,
      unitCap: ENTERPRISE_TIER.unitCap,
      stripePriceId: ENTERPRISE_TIER.stripePriceId,
      tagline: ENTERPRISE_TIER.tagline,
      features: ENTERPRISE_TIER.features,
      contactUrl: ENTERPRISE_TIER.contactUrl,
      ctaLabel: ENTERPRISE_TIER.ctaLabel,
    },
  };
}

/** @deprecated Use getDisplayPricingLadder instead. Returns PRICING_2026 for backward compat. */
export function getMarket2026DisplaySchedule() {
  return PRICING_2026;
}

/**
 * Single tier to show when marketing only your Rental Command Center (e.g. /platform).
 * Uses {@link TierKey.SCALE} when the active schedule includes it; otherwise the
 * tier with the highest door cap (e.g. legacy `growth`).
 */
export function getFullOperatingSystemTierKey(
  pricingTierGroup = "market_2026",
  asOfDate = new Date()
) {
  const schedule = getPricingSchedule(pricingTierGroup, asOfDate);
  if (schedule[TierKey.SCALE]) return TierKey.SCALE;
  const keys = Object.keys(schedule).filter(
    (k) => schedule[k] && typeof schedule[k].monthlyUSD === "number"
  );
  if (keys.length === 0) return TierKey.STARTER;
  return keys.reduce((best, k) => {
    const cap = Number(schedule[k]?.unitCap) || 0;
    const bestCap = Number(schedule[best]?.unitCap) || 0;
    return cap > bestCap ? k : best;
  }, keys[0]);
}
